<template>
  <div class="d-flex align-center ga-2">
    <v-btn color="primary" variant="tonal" @click="openDialog">
      <v-icon start>mdi-account-multiple</v-icon>
      {{ selectedIds.length ? `${selectedIds.length} selected` : "Select accounts" }}
    </v-btn>
    <v-btn
      color="primary"
      :loading="syncing"
      :disabled="syncing || !selectedIds.length"
      aria-label="Sync selected accounts"
      @click="syncSelected"
    >
      <v-icon>mdi-sync</v-icon>
    </v-btn>

    <v-dialog v-model="dialog" max-width="680" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Select platform accounts</span>
          <v-btn variant="text" color="primary" @click="selectAll">Select all</v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-skeleton-loader v-if="loading" type="list-item@5" />
          <v-alert v-else-if="loadError" type="error" variant="tonal">
            {{ loadError }}
            <template #append>
              <v-btn variant="text" :loading="loading" @click="loadAccounts">Retry</v-btn>
            </template>
          </v-alert>
          <div v-else v-for="(accounts, platform) in groupedAccounts" :key="platform" class="mb-4">
            <div class="d-flex align-center ga-2 text-subtitle-1 font-weight-bold mb-1">
              <v-icon>{{ platformIcon(platform) }}</v-icon>
              {{ platformLabel(platform) }}
            </div>
            <v-checkbox
              v-for="account in accounts"
              :key="account.id"
              v-model="draftIds"
              :value="account.id"
              :label="account.account_name"
              hide-details
              density="compact"
            />
          </div>
          <v-alert v-if="selectionError" type="warning" variant="tonal" class="mt-3">
            {{ selectionError }}
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-space-between">
          <v-btn variant="text" @click="draftIds = []">Clear</v-btn>
          <div>
            <v-btn variant="text" @click="cancel">Cancel</v-btn>
            <v-btn color="primary" @click="applySelection">Done</v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSyncResult" max-width="560">
      <v-card>
        <v-card-title>Synchronization result</v-card-title>
        <v-card-text>
          <v-alert v-if="syncValidationError" type="error" variant="tonal" class="mb-3">
            {{ syncValidationError }}
          </v-alert>
          <div v-if="syncResult.synced?.length" class="mb-3">
            <strong>Synced</strong>
            <div v-for="item in syncResult.synced" :key="syncItemKey(item)" class="text-success">
              {{ syncItemLabel(item) }}
            </div>
          </div>
          <div v-if="syncResult.failed?.length">
            <strong>Failed</strong>
            <div v-for="item in syncResult.failed" :key="syncItemKey(item)" class="text-error">
              {{ syncItemLabel(item) }}: {{ item.reason || item.message || "Unknown error" }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="justify-end"><v-btn @click="showSyncResult = false">Close</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { messageCenterApi } from "@/core/messageCenterApi";
import { toast } from "vue3-toastify";

const emit = defineEmits(["accounts-selected", "sync-complete"]);
const accounts = ref([]);
const selectedIds = ref([]);
const draftIds = ref([]);
const dialog = ref(false);
const loading = ref(true);
const syncing = ref(false);
const loadError = ref("");
const selectionError = ref("");
const syncValidationError = ref("");
const syncResult = ref({ synced: [], failed: [] });
const showSyncResult = ref(false);

const accountLoadError = (error) => {
  if (error.response?.status >= 500) return "The accounts service is temporarily unavailable. Please try again.";
  if (!error.response) return "Could not reach the accounts service. Please check your connection and try again.";
  return error.response.data?.message || error.response.data?.msg || "Unable to load accounts.";
};

const groupedAccounts = computed(() => accounts.value.reduce((groups, account) => {
  const platform = account.platform || "other";
  (groups[platform] ||= []).push(account);
  return groups;
}, {}));

const platformLabel = (platform) => ({ facebook_messenger: "Facebook", instagram: "Instagram", whatsapp: "WhatsApp" }[platform] || platform);
const platformIcon = (platform) => ({ facebook_messenger: "mdi-facebook", instagram: "mdi-instagram", whatsapp: "mdi-whatsapp" }[platform] || "mdi-message-text");
const unique = (ids) => [...new Set(ids.map(Number).filter(Number.isFinite))];
const selectedAccounts = () => accounts.value.filter((account) => selectedIds.value.includes(Number(account.id)));

const selectAll = () => { draftIds.value = unique(accounts.value.map(({ id }) => id)); };
const cancel = () => { draftIds.value = [...selectedIds.value]; dialog.value = false; selectionError.value = ""; };
const applySelection = () => {
  const ids = unique(draftIds.value);
  if (!ids.length) { selectionError.value = "Select at least one account."; return; }
  selectionError.value = "";
  selectedIds.value = ids;
  localStorage.setItem("selectedDmAccountIds", JSON.stringify(ids));
  emit("accounts-selected", selectedAccounts());
  dialog.value = false;
};

const syncItemKey = (item) => typeof item === "object" ? (item.platform_account_id || item.id || JSON.stringify(item)) : item;
const syncItemLabel = (item) => typeof item === "object" ? (item.account_name || item.name || `Account ${item.platform_account_id || item.id}`) : `Account ${item}`;
const loadAccounts = async () => {
  loading.value = true;
  loadError.value = "";
  try {
    accounts.value = await messageCenterApi.getDmPages();
    const stored = unique(JSON.parse(localStorage.getItem("selectedDmAccountIds") || "[]"));
    const available = new Set(accounts.value.map(({ id }) => Number(id)));
    selectedIds.value = stored.filter((id) => available.has(id));
    draftIds.value = [...selectedIds.value];
    if (selectedIds.value.length) emit("accounts-selected", selectedAccounts());
  } catch (error) {
    loadError.value = accountLoadError(error);
  } finally {
    loading.value = false;
  }
};
const openDialog = async () => {
  dialog.value = true;
  if (loadError.value) await loadAccounts();
};
const syncSelected = async () => {
  syncing.value = true;
  syncValidationError.value = "";
  try {
    const response = await messageCenterApi.syncAccounts(selectedIds.value);
    syncResult.value = response.data || response || { synced: [], failed: [] };
    showSyncResult.value = true;
    emit("sync-complete");
  } catch (error) {
    const errors = error.response?.status === 422 ? error.response?.data?.errors?.account_ids : null;
    syncValidationError.value = Array.isArray(errors) ? errors.join(" ") : (errors || error.response?.data?.message || "Synchronization failed.");
    showSyncResult.value = true;
  } finally { syncing.value = false; }
};

const refreshAvailableAccounts = async () => {
  const previousIds = [...selectedIds.value];
  accounts.value = await messageCenterApi.getDmPages();
  const availableIds = new Set(accounts.value.map(({ id }) => Number(id)));
  selectedIds.value = previousIds.filter((id) => availableIds.has(id));
  draftIds.value = [...selectedIds.value];
  localStorage.setItem("selectedDmAccountIds", JSON.stringify(selectedIds.value));
  toast("بعض الحسابات لم تعد متاحة، تم تحديث القائمة", { type: "warning" });
  return selectedAccounts();
};

onMounted(async () => {
  await loadAccounts();
  if (!selectedIds.value.length) dialog.value = true;
});

defineExpose({ refreshAvailableAccounts });
</script>
